import { ReactNode, useContext } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/utils/classes";
import { TabsContext } from "./tabs-context";
import { PadlockIcon } from "@/assets/icons";

/* Variants */
const tabVariants = cva("tab font-semibold", {
  variants: {
    active: {
      true: "tab-active",
      false: "",
    },
    disabled: {
      true: "",
      false: "",
    },
  },
});

/* Prop Types */
export interface TabProps extends React.ButtonHTMLAttributes<HTMLAnchorElement>, VariantProps<typeof tabVariants> {
  value: string | number;
  children: ReactNode;
  disabled?: boolean;
}

/*
 * Tab Component
 *
 * Individual tab button for the Tabs component.
 */
export const Tab = ({ value, children, disabled, className, onClick, ...props }: TabProps) => {
  /* Context */
  const context = useContext(TabsContext);

  if (!context) {
    throw new Error("Tab must be used within a Tabs component");
  }

  /* Consts */
  const { activeTab, setActiveTab, showActiveBg } = context;
  const isActive = activeTab === value;

  /* Handlers */
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onClick?.(e);
    if (!disabled) {
      setActiveTab(value);
    }
  };

  /* Return */
  return (
    <a
      {...props}
      role="tab"
      className={cn(tabVariants({ active: isActive, disabled }), showActiveBg && isActive && "[--tab-bg:theme('colors.base-200')]", className)}
      onClick={handleClick}
      aria-selected={isActive}
      aria-disabled={disabled}
    >
      {children}
      {disabled && <PadlockIcon className="ml-2" />}
    </a>
  );
};
